import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api/client';
import { CONFIG_FILES, type ConfigFileKey } from './types';

/**
 * The blob-storage config editors.
 *
 * `utility/file/{container}/{file}` answers with the file's text in `data`, and
 * `utility/upload` overwrites it and answers with the text it read back.
 *
 * A file that does not exist comes back as a success with an empty `data`
 * rather than an error, which is why "does this file exist" is a question about
 * the payload and not about the status.
 */

const KEY = ['configuration'] as const;

const fileOf = (key: ConfigFileKey) =>
  CONFIG_FILES.find((entry) => entry.key === key)!;

/**
 * One file's query. Shared with readers outside this page (the sidebar reads
 * `server.json`), so a save here updates them too.
 */
export function configFileQuery(key: ConfigFileKey) {
  const { container, file } = fileOf(key);
  return {
    queryKey: [...KEY, key],
    queryFn: () => get<string | null>(`utility/file/${container}/${file}`),
  };
}

export function useConfigFile(key: ConfigFileKey) {
  return useQuery({
    ...configFileQuery(key),
    // Configuration is read to be changed, so a stale copy is worse than a
    // second request.
    staleTime: 0,
  });
}

/**
 * Which of the three files actually exist.
 *
 * The original asked this from the *sidebar*, on every render for every super
 * admin, to decide whether to show three menu entries — three requests to
 * decide whether to draw three rows. There is one menu entry now, and the
 * question is asked once, by the page that needs the answer.
 */
export function useAvailableConfigFiles() {
  const results = useQueries({
    queries: CONFIG_FILES.map(({ key }) => ({
      ...configFileQuery(key),
      staleTime: 60_000,
    })),
  });

  return {
    isPending: results.some((result) => result.isPending),
    available: CONFIG_FILES.filter(
      (_, index) => !!results[index].data
    ).map((entry) => entry.key),
  };
}

export function useSaveConfigFile(key: ConfigFileKey) {
  const queryClient = useQueryClient();
  const { container, file } = fileOf(key);

  return useMutation({
    mutationFn: (text: string) =>
      post<string | null>('utility/upload', {
        containerName: container,
        fileName: file,
        text,
      }),
    // The endpoint answers with the file as re-read, so the cache takes that
    // rather than asking again for what was just written.
    onSuccess: (written) => queryClient.setQueryData([...KEY, key], written),
  });
}
